import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getTenantLaunchReadiness } from '../api/launchReadinessApi';
import {
  getTenantProvisioningPlan,
  getTenantProvisioningRuns,
  getTenantProvisioningEvents,
  getTenantDatabaseHealthChecks,
  runTenantProvisioningHealthCheck,
  rollbackTenantProvisioningRun,
  startTenantProvisioningRun,
  verifyTenantDatabaseConnection,
  verifyTenantTestSeed,
} from '../api/tenantProvisioningApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens } from '../app/designTokens';
import { DataTable } from './DataTable';
import { DetailField } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/Button';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import {
  formatLaunchStatus,
  formatProvisioningEventMessage,
  formatProvisioningMode,
  formatProvisioningStatus,
  formatProvisioningStepTitle,
  formatReadinessMessage,
  safeTranslate,
} from '../utils/systemLabels';

const { semantic } = colorTokens;

function portalProvisioningMessage(t, messageKey) {
  const keyMap = {
    customerSafePreparing: 'customerSafePreparing',
    customerSafeReady: 'customerSafeReady',
    customerSafeAttention: 'customerSafeAttention',
    provisioning_complete: 'customerSafeReady',
    provisioning_attention_required: 'customerSafeAttention',
    provisioning_in_progress: 'customerSafePreparing',
    provisioning_record_only: 'customerSafePreparing',
  };
  const i18nKey = keyMap[messageKey] || 'customerSafePreparing';
  return t(`tenantProvisioning:${i18nKey}`);
}

export function TenantProvisioningTab({ tenantId, launchReadinessTabIndex = 3 }) {
  const { t } = useTranslation(['tenantProvisioning', 'common', 'statuses', 'launchReadiness']);
  const { session } = useAuth();
  const canRun = hasPermission(session, 'system.tenant_provisioning.run')
    || hasPermission(session, 'system.tenants.provisioning.run');
  const canVerify = hasPermission(session, 'system.tenants.database.verify')
    || canRun;
  const canRollback = hasPermission(session, 'system.tenant_provisioning.rollback');

  const [plan, setPlan] = useState(null);
  const [runs, setRuns] = useState([]);
  const [events, setEvents] = useState([]);
  const [healthChecks, setHealthChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launchReadiness, setLaunchReadiness] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planRes, runsRes, eventsRes, healthRes, launchRes] = await Promise.all([
        getTenantProvisioningPlan(tenantId),
        getTenantProvisioningRuns(tenantId, { per_page: 10 }),
        getTenantProvisioningEvents(tenantId, { per_page: 20 }).catch(() => ({ data: [] })),
        getTenantDatabaseHealthChecks(tenantId, { per_page: 10 }).catch(() => ({ data: [] })),
        getTenantLaunchReadiness(tenantId).catch(() => null),
      ]);
      setPlan(planRes.data || planRes);
      setRuns(runsRes.data || []);
      setEvents(eventsRes.data || []);
      setHealthChecks(healthRes.data || []);
      setLaunchReadiness(launchRes);
    } catch {
      setError(t('tenantProvisioning:tenant_tab.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => { load(); }, [load]);

  const latestRun = runs[0];
  const capabilities = plan?.capabilities || {};
  const dbCreationEnabled = capabilities.database_creation_enabled;
  const testSeedEnabled = capabilities.test_seed_enabled;
  const productionGuard = capabilities.production_guard_active
    && !capabilities.db_creation_allow_production;

  const runAction = async (action) => {
    setMessage('');
    setError('');
    try {
      await action();
      setMessage(t('tenantProvisioning:tenant_tab.action_success'));
      load();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('tenantProvisioning:tenant_tab.action_failed')));
    }
  };

  const stepColumns = useMemo(() => [
    {
      id: 'step_code',
      label: t('tenantProvisioning:tenant_tab.columns.step'),
      render: (row) => formatProvisioningStepTitle(row.step_code, t),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => <StatusBadge status={row.status} category="provisioning_step" size="Sm" />,
    },
    { id: 'completed_at', label: t('tenantProvisioning:tenant_tab.columns.completed_at') },
  ], [t]);

  const eventColumns = useMemo(() => [
    {
      id: 'event_type',
      label: t('tenantProvisioning:events.columns.event'),
      render: (row) => safeTranslate(t, `tenantProvisioning:event_types.${row.event_type}`, row.event_type),
    },
    {
      id: 'message',
      label: t('tenantProvisioning:events.columns.message'),
      render: (row) => formatProvisioningEventMessage(row) || t('common:empty_value'),
    },
    { id: 'created_at', label: t('tenantProvisioning:events.columns.at') },
  ], [t]);

  const healthColumns = useMemo(() => [
    {
      id: 'status',
      label: t('common:status'),
      render: (row) => <StatusBadge status={row.status} category="health_check" size="Sm" />,
    },
    {
      id: 'message',
      label: t('common:message'),
      render: (row) => formatReadinessMessage({
        message: row.message,
        message_key: row.details?.message_key || row.message_key,
      }) || row.message || t('common:empty_value'),
    },
    { id: 'checked_at', label: t('tenantProvisioning:health.columns.checked_at') },
  ], [t]);

  if (loading) return <LoadingSkeleton type="Detail" />;

  const launchTone = launchReadiness?.status === 'no_go'
    ? 'Danger'
    : launchReadiness?.status === 'go_with_warnings'
      ? 'Warning'
      : 'Default';

  return (
    <Stack spacing={2.5}>
      {launchReadiness && (
        <SectionCard
          variant={launchTone === 'Default' ? 'Default' : launchTone}
          title={t('launchReadiness:provisioningLatestStatus')}
          sx={{ mb: 0 }}
        >
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
              <StatusBadge
                status={launchReadiness.status}
                category="generic"
                apiLabel={formatLaunchStatus(launchReadiness.status)}
                size="Sm"
                tone={
                  launchReadiness.status === 'no_go' ? 'Danger'
                    : launchReadiness.status === 'go_with_warnings' ? 'Warning'
                      : 'Success'
                }
              />
            </Box>
            <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
              {t('launchReadiness:openGoNoGoTabWithIndex', { index: launchReadinessTabIndex + 1 })}
            </Typography>
          </Stack>
        </SectionCard>
      )}

      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      {productionGuard && (
        <SectionCard variant="Danger" sx={{ mb: 0 }}>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('tenantProvisioning:productionGuard')}
          </Typography>
        </SectionCard>
      )}

      {plan && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <StatusBadge
              status={dbCreationEnabled ? 'enabled' : 'disabled'}
              category="generic"
              apiLabel={dbCreationEnabled
                ? t('tenantProvisioning:dbCreationEnabled')
                : t('tenantProvisioning:dbCreationDisabled')}
              size="Sm"
              tone={dbCreationEnabled ? 'Success' : 'Neutral'}
            />
            <StatusBadge
              status={testSeedEnabled ? 'enabled' : 'disabled'}
              category="generic"
              apiLabel={testSeedEnabled
                ? t('tenantProvisioning:testSeedEnabled')
                : t('tenantProvisioning:testSeedDisabled')}
              size="Sm"
              tone={testSeedEnabled ? 'Success' : 'Neutral'}
            />
            <StatusBadge
              status={capabilities.db_creation_mode || plan.mode || 'mode'}
              category="generic"
              apiLabel={`${t('tenantProvisioning:dbCreationMode')}: ${formatProvisioningMode(capabilities.db_creation_mode || plan.mode) || capabilities.db_creation_mode || plan.mode}`}
              size="Sm"
              tone="Info"
            />
          </Stack>

          {!dbCreationEnabled && (
            <SectionCard variant="Warning" sx={{ mb: 0 }}>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('tenantProvisioning:dryRunOnly')}
              </Typography>
            </SectionCard>
          )}

          <SectionCard variant="Default" title={t('tenantProvisioning:tenant_tab.plan_title')} sx={{ mb: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <DetailField
                label={t('tenantProvisioning:tenant_tab.mode')}
                value={formatProvisioningMode(plan.mode) || plan.mode}
              />
              <DetailField
                label={t('tenantProvisioning:databaseNameValid')}
                value={plan.database_name}
              />
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 12, color: semantic.text.muted }}>
              {t('tenantProvisioning:centralDatabaseProtection')}
            </Typography>
          </SectionCard>
        </>
      )}

      {latestRun && (
        <SectionCard variant="Default" title={t('tenantProvisioning:latestRun')} sx={{ mb: 0 }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
              <StatusBadge status={latestRun.status} category="provisioning_run" size="Sm" />
            </Box>
            <DetailField
              label={t('common:status')}
              value={`${formatProvisioningStatus(latestRun.run_type) || latestRun.run_type} / ${formatProvisioningMode(latestRun.mode) || latestRun.mode}`}
            />
            {latestRun.created_at && (
              <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
                {latestRun.created_at} — {latestRun.completed_at || t('common:empty_value')}
              </Typography>
            )}
          </Stack>
        </SectionCard>
      )}

      {canRun && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="Secondary"
            onClick={() => runAction(() => startTenantProvisioningRun(tenantId, { mode: 'dry_run', dry_run: true }))}
          >
            {t('tenantProvisioning:actions.dry_run')}
          </Button>
          <Button
            variant="Primary"
            disabled={!dbCreationEnabled || productionGuard}
            onClick={() => runAction(() => startTenantProvisioningRun(tenantId, { mode: 'create_database', create_database: true }))}
          >
            {t('tenantProvisioning:createDatabase')}
          </Button>
          <Button
            variant="Accent"
            disabled={!dbCreationEnabled || !testSeedEnabled || productionGuard}
            onClick={() => runAction(() => startTenantProvisioningRun(tenantId, { mode: 'create_database_with_test_seed', create_database: true, test_seed: true }))}
          >
            {t('tenantProvisioning:createDatabaseWithTestSeed')}
          </Button>
          {canVerify && (
            <>
              <Button
                variant="Secondary"
                onClick={() => runAction(() => verifyTenantDatabaseConnection(tenantId))}
              >
                {t('tenantProvisioning:verifyConnection')}
              </Button>
              <Button
                variant="Secondary"
                disabled={!testSeedEnabled}
                onClick={() => runAction(() => verifyTenantTestSeed(tenantId))}
              >
                {t('tenantProvisioning:verifyTestSeed')}
              </Button>
            </>
          )}
          <Button
            variant="Secondary"
            onClick={() => runAction(() => runTenantProvisioningHealthCheck(tenantId))}
          >
            {t('tenantProvisioning:actions.health_check')}
          </Button>
          {canRollback && latestRun && (
            <Button
              variant="Danger"
              onClick={() => runAction(() => rollbackTenantProvisioningRun(tenantId, latestRun.id, { reason: t('tenantProvisioning:actions.rollback_reason') }))}
            >
              {t('tenantProvisioning:actions.rollback')}
            </Button>
          )}
        </Stack>
      )}

      {latestRun?.steps && (
        <SectionCard variant="Default" title={t('tenantProvisioning:steps_heading')} noPadding sx={{ mb: 0 }}>
          <DataTable
            type="Compact"
            embedded
            columns={stepColumns}
            rows={latestRun.steps}
            getRowKey={(row) => row.id}
            emptyMessage={t('tenantProvisioning:tenant_tab.no_steps')}
          />
        </SectionCard>
      )}

      <SectionCard variant="Default" title={t('tenantProvisioning:tenant_tab.events_title')} noPadding sx={{ mb: 0 }}>
        <DataTable
          type="Compact"
          embedded
          columns={eventColumns}
          rows={events}
          getRowKey={(row) => row.id}
          emptyMessage={t('tenantProvisioning:tenant_tab.no_events')}
        />
      </SectionCard>

      <SectionCard variant="Default" title={t('tenantProvisioning:tenant_tab.health_title')} noPadding sx={{ mb: 0 }}>
        <DataTable
          type="Compact"
          embedded
          columns={healthColumns}
          rows={healthChecks}
          getRowKey={(row) => row.id}
          emptyMessage={t('tenantProvisioning:health.no_checks')}
        />
      </SectionCard>
    </Stack>
  );
}

export { portalProvisioningMessage };
